// date format: d MMM yyyy, H:mm, time should be specifed based on UTC time

import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { ARBITRUM, MEGAETH } from "config/chains";

import ExternalLink from "components/ExternalLink/ExternalLink";
import TokenIcon from "components/TokenIcon/TokenIcon";

import sparkleIcon from "img/sparkle.svg";

export type EventData = {
  id: string;
  title: ReactNode;
  isActive?: boolean;
  /**
   * KLI UI flag ID. When present, overrides `isActive` — visibility is controlled by KLI.
   */
  flagId?: string;
  startDate?: string;
  endDate: string;
  bodyText: ReactNode;
  variant?: "info" | "warning";
  chains?: number[];
  link?: {
    text: string;
    href: string;
    /**
     * @default false
     */
    newTab?: boolean;
  };
};

export const homeEventsData: EventData[] = [];

export const appEventsData: EventData[] = [
  {
    id: "spcx-pre-ipo-arbitrum-listing-updated",
    isActive: true,
    startDate: "09 Jun 2026, 12:00",
    endDate: "16 Jun 2026, 12:00",
    flagId: "showSpcxPreIpoArbitrumListingUpdated",
    chains: [ARBITRUM],
    title: "SPCX Pre-IPO market added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> pre-IPO SpaceX perpetuals with up to 10x leverage, 24/7. SPCX/USD is a new type
        of market for GMX, launching with limited initial OI capacity. Caps and trading parameters may change around the
        IPO, and opening or increasing positions may be unavailable when caps are reached.{" "}
        <ExternalLink href="https://x.com/GMX_IO/status/2064418523657470193">Read more</ExternalLink>.
      </>
    ),
  },
  {
    id: "mega-arbitrum-megaeth-listing",
    flagId: "showMegaListingArbitrumMegaeth",
    endDate: "07 May 2026, 12:00",
    chains: [ARBITRUM, MEGAETH],
    title: "MEGA market added on Arbitrum and MegaETH",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> MEGA, or <Link to="/pools">provide liquidity</Link> using GM, GLV{" "}
        <span className="text-slate-100">[WETH-USDC]</span> on Arbitrum, or GLV{" "}
        <span className="text-slate-100">[USDM-USDM]</span> on MegaETH.
      </>
    ),
  },
  {
    id: "megaeth-points-program",
    flagId: "showMegaethPoints",
    endDate: "31 Dec 2026, 0:00",
    chains: [MEGAETH],
    title: "Earn points on GMX MegaETH",
    bodyText: (
      <span className="block">
        <span className="mb-12 block text-slate-100">Earn points each epoch across four activities:</span>
        <span className="grid grid-cols-[auto_1fr] items-start gap-x-8 gap-y-12">
          <img className="mt-3 h-12" src={sparkleIcon} alt="" />
          <span>
            <span className="block font-medium">Trading activity</span>
            <span className="block text-12 text-slate-100">Based on cumulative trading volume</span>
          </span>
          <img className="mt-3 h-12" src={sparkleIcon} alt="" />
          <span>
            <span className="block font-medium">Referral volume</span>
            <span className="block text-12 text-slate-100">Trading volume from wallets using your referral code</span>
          </span>
          <img className="mt-3 h-12" src={sparkleIcon} alt="" />
          <span>
            <span className="block font-medium">Trader PnL</span>
            <span className="block text-12 text-slate-100">Net positive realized PnL only, to reward skill</span>
          </span>
          <img className="mt-3 h-12" src={sparkleIcon} alt="" />
          <span>
            <span className="block font-medium">
              GLV <span className="text-slate-100">[USDM-USDM]</span> liquidity
            </span>
            <span className="block text-12 text-slate-100">Time-weighted share of the vault over the epoch</span>
          </span>
        </span>
      </span>
    ),
  },
  {
    id: "gold-silver-fee-reduction",
    flagId: "showGoldSilverFeeReduction",
    endDate: "21 Apr 2026, 12:00",
    title: "GOLD and SILVER trading fees heavily reduced",
    bodyText: (
      <>
        Position fees for GOLD/USD and SILVER/USD have been lowered to 1/2 bps from 4/6 bps during on-hours.{" "}
        <ExternalLink href="https://docs.gmx.io/docs/trading/overview/#fees">Read more</ExternalLink>.
      </>
    ),
  },
  {
    id: "gold-silver-arbitrum-listing",
    isActive: true,
    startDate: "10 Apr 2026, 12:00",
    endDate: "17 Apr 2026, 12:00",
    title: "GOLD and SILVER commodity markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> GOLD and SILVER perpetuals 24/7 with up to 100x leverage, or{" "}
        <Link to="/pools">provide liquidity</Link> via GLV <span className="text-slate-100">[WETH-USDC]</span>. Find
        them under the TradFi category in the market dropdown.
      </>
    ),
  },
  {
    id: "energy-markets-arbitrum-listing",
    flagId: "showEnergyMarketsArbitrumListing",
    endDate: "30 Apr 2026, 12:00",
    title: "WTI Crude Oil, Brent Crude Oil and Natural Gas energy commodity markets added on Arbitrum",
    bodyText: (
      <>
        Trade WTIOIL, BRENTOIL (up to 100x leverage) and NATGAS (up to 40x leverage) perpetuals 24/7, or{" "}
        <Link to="/pools">provide liquidity</Link> via GLV <span className="text-slate-100">[WETH-USDC]</span>.{" "}
        <ExternalLink href="https://docs.gmx.io/docs/trading/overview/#rwa-and-commodity-markets">
          Read more
        </ExternalLink>
        .
      </>
    ),
  },
  {
    id: "megaeth-launch",
    isActive: true,
    startDate: "03 Apr 2026, 0:00",
    endDate: "10 Apr 2026, 16:00",
    title: "GMX is now live on MegaETH",
    bodyText: (
      <>
        Trade perpetuals, create and share your referral code, and provide liquidity on MegaETH using its native
        stablecoin: USDm.{" "}
        <ExternalLink href="https://gmxio.substack.com/p/gmx-is-now-live-on-megaeth-trade">Read more</ExternalLink>.
      </>
    ),
  },
  {
    id: "cc-met-arbitrum-listing",
    isActive: true,
    startDate: "13 Feb 2026, 14:00",
    endDate: "20 Feb 2026, 14:00",
    title: "CC (Canton) and MET (Meteora) markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM or GLV{" "}
        <span className="text-slate-100">[WBTC-USDC]</span>.
      </>
    ),
  },
  {
    id: "xaut-lit-ip-arbitrum-listing",
    isActive: true,
    startDate: "23 Jan 2026, 11:00",
    endDate: "30 Jan 2026, 10:00",
    title: "XAUT (Tether Gold), LIT and IP markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM, GLV{" "}
        <span className="text-slate-100">[WBTC-USDC]</span>, or GLV <span className="text-slate-100">[WETH-USDC]</span>
      </>
    ),
  },
  {
    id: "listing-01-09-26",
    isActive: true,
    startDate: "09 Jan 2026, 10:00",
    endDate: "16 Jan 2026, 12:00",
    title: "AR, DASH, JTO, SYRUP and CHZ markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM, GLV{" "}
        <span className="text-slate-100">[WBTC-USDC]</span>, or GLV <span className="text-slate-100">[WETH-USDC]</span>
      </>
    ),
  },
  {
    id: "mon-sky-zec-listing",
    isActive: true,
    startDate: "22 Dec 2025, 16:10",
    endDate: "29 Dec 2025, 16:10",
    title: "MON (Monad), SKY and ZEC (Zcash) markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM, GLV{" "}
        <span className="text-slate-100">[WBTC-USDC]</span>, or GLV <span className="text-slate-100">[WETH-USDC]</span>
      </>
    ),
  },
  {
    id: "open-interest-calculation-update",
    isActive: true,
    startDate: "19 Dec 2025, 08:00",
    endDate: "26 Dec 2025, 08:00",
    title: "Open Interest Calculation Update",
    bodyText: (
      <>
        From 22nd December, open interest will be tracked in token amounts instead of USD values for improved balance
        accuracy. <ExternalLink href="https://t.me/GMX_Announcements/1175">Read more</ExternalLink>.
      </>
    ),
  },
  {
    id: "xaut0-avalanche-listing",
    isActive: true,
    startDate: "17 Oct 2025, 10:00",
    endDate: "24 Oct 2025, 10:00",
    title: "XAUt0 markets added on Avalanche",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM{" "}
        <span className="text-slate-100">[XAUt0-XAUt0]</span> or GM <span className="text-slate-100">[XAUt0-USDT]</span>
      </>
    ),
  },
  {
    id: "morpho-glv-lending",
    isActive: true,
    startDate: "14 Oct 2025, 6:00",
    endDate: "21 Oct 2025, 6:00",
    title: "Morpho now supports GLV",
    bodyText: (
      <>
        Lending and borrowing are now available for GLV assets:{" "}
        <TokenIcon symbol="GLV" displaySize={16} className="mb-4" /> <span className="text-slate-100">[BTC-USDC]</span>{" "}
        and <TokenIcon symbol="GLV" displaySize={16} className="mb-4" />{" "}
        <span className="text-slate-100">[ETH-USDC]</span> on{" "}
        <ExternalLink href="https://app.morpho.org/arbitrum/borrow?search=GLV">Morpho</ExternalLink>
      </>
    ),
  },
  {
    id: "aster-0g-avnt-linea-listing",
    isActive: true,
    startDate: "09 Oct 2025, 14:30",
    endDate: "16 Oct 2025, 12:00",
    title: "0G, ASTER, AVNT and LINEA markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM, GLV{" "}
        <span className="text-slate-100">[WETH-USDC]</span>, or GLV <span className="text-slate-100">[WBTC-USDC]</span>
      </>
    ),
  },
  {
    id: "xpl-bnb-sol-listing",
    isActive: true,
    startDate: "25 Sep 2025, 16:50",
    endDate: "02 Oct 2025, 18:00",
    title: "XPL (Plasma), BNB and SOL markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM or GLV{" "}
        <span className="text-slate-100">[WBTC-USDC]</span>
      </>
    ),
  },
  {
    id: "zora-kta-listing",
    isActive: true,
    startDate: "18 Sep 2025, 14:00",
    endDate: "25 Sep 2025, 12:00",
    title: "ZORA and KTA markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM or GLV{" "}
        <span className="text-slate-100">[WETH-USDC]</span>
      </>
    ),
  },
  {
    id: "new-interface-and-price-impact-improvements",
    isActive: true,
    startDate: "08 Sep 2025, 12:00",
    endDate: "22 Sep 2025, 12:00",
    title: "New interface and price impact improvements",
    bodyText: (
      <>
        The app has a revamped interface, including a new light theme. Price impact is now capped and charged only on
        position close. <ExternalLink href="https://x.com/GMX_IO/status/1965077965236056467">Read more</ExternalLink>.
      </>
    ),
  },
  {
    id: "listing-09-04",
    isActive: true,
    startDate: "04 Sep 2025, 10:00",
    endDate: "11 Sep 2025, 12:00",
    title: "LINK, MORPHO, VVV and WELL markets added on Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM or GLV{" "}
        <span className="text-slate-100">[WETH-USDC]</span>
      </>
    ),
  },
  {
    id: "wlfi-listing",
    isActive: true,
    startDate: "01 Sep 2025, 12:00",
    endDate: "07 Sep 2025, 12:00",
    title: "WLFI market added on Avalanche and Arbitrum",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> this market, or <Link to="/pools">provide liquidity</Link> using GM or GLV{" "}
        <span className="text-slate-100">[WAVAX-USDC]</span> for WLFI on Avalanche, and GM or GLV{" "}
        <span className="text-slate-100">[WETH-USDC]</span> on Arbitrum
      </>
    ),
  },
  {
    id: "aero-brett-pbtc-listing",
    isActive: true,
    startDate: "28 Aug 2025, 10:00",
    endDate: "04 Sep 2025, 12:00",
    title: "AERO and BRETT markets added on Arbitrum, BTC market added on Botanix",
    bodyText: (
      <>
        <Link to="/trade">Trade</Link> these markets, or <Link to="/pools">provide liquidity</Link> using GM or GLV{" "}
        <span className="text-slate-100">[WETH-USDC]</span> for AERO and BRETT, or GM{" "}
        <span className="text-slate-100">[PBTC]</span> for BTC
      </>
    ),
  },
];
